import { SecureAuthService } from '../auth/secureAuthService';

export interface ApiRequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
    headers?: Record<string, string>;
    body?: any;
    scopes?: string[];
    timeout?: number;
    retries?: number;
    useIdToken?: boolean;
    requireAuth?: boolean;
}

export interface ApiResponse<T = any> {
    data: T;
    status: number;
    statusText: string;
    headers: Headers;
}

export class ApiError extends Error {
    constructor(
        message: string,
        public status: number,
        public statusText: string,
        public response?: Response
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

/**
 * Servicio API seguro que maneja tokens automáticamente
 * con renovación automática y reintentos
 */
export class SecureApiService {
    private baseUrl: string;
    private defaultScopes: string[];
    private authService: SecureAuthService;
    private defaultTimeout: number = 30000; // 30 segundos
    private defaultRetries: number = 3;
    private defaultUseIdToken: boolean = false; // Por defecto usar access token

    constructor(
        authService: SecureAuthService,
        baseUrl: string = '',
        defaultScopes: string[] = ['User.Read'],
        useIdToken: boolean = false
    ) {
        this.authService = authService;
        this.baseUrl = baseUrl;
        this.defaultScopes = defaultScopes;
        this.defaultUseIdToken = useIdToken;
    }

    /**
     * Crea un AbortController con timeout
     */
    private createTimeoutController(timeout: number): AbortController {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), timeout);
        return controller;
    }

    /**
     * Espera un tiempo determinado (para reintentos)
     */
    private async delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Calcula el tiempo de espera para reintentos (exponential backoff)
     */
    private getRetryDelay(attempt: number): number {
        return Math.min(1000 * Math.pow(2, attempt), 10000); // Max 10 segundos
    }

    /**
     * Obtiene un token válido según la configuración
     */
    private async getValidToken(useIdToken: boolean, scopes: string[]): Promise<string> {
        const token = useIdToken
            ? await this.authService.getValidIdToken(scopes)
            : await this.authService.getValidAccessToken(scopes);

        if (!token) {
            throw new Error(`No valid ${useIdToken ? 'ID' : 'access'} token available`);
        }

        return token;
    }

    /**
     * Prepara las opciones de la petición HTTP
     */
    private prepareRequestOptions(
        method: string,
        headers: Record<string, string>,
        body: any,
        token: string | null,
        timeout: number
    ): RequestInit {
        const baseHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...headers
        };

        if (token) {
            baseHeaders['Authorization'] = `Bearer ${token}`;
        }

        const requestHeaders = new Headers(baseHeaders);

        const controller = this.createTimeoutController(timeout);
        const requestOptions: RequestInit = {
            method,
            headers: requestHeaders,
            signal: controller.signal,
        };

        if (body && method !== 'GET' && method !== 'DELETE') {
            requestOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
        }

        return requestOptions;
    }

    /**
     * Construye la URL completa para la petición
     */
    private buildUrl(endpoint: string): string {
        return endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    }

    /**
     * Maneja errores de respuesta HTTP específicos
     */
    private async handleHttpError(
        response: Response,
        attempt: number,
        retries: number,
        useIdToken: boolean,
        scopes: string[]
    ): Promise<boolean> {
        if (response.status === 401 && attempt < retries) {
            console.warn(`401 Unauthorized, attempting ${useIdToken ? 'ID' : 'access'} token refresh (attempt ${attempt + 1})`);
            if (useIdToken) {
                await this.authService.forceIdTokenRefresh(scopes);
            } else {
                await this.authService.forceTokenRefresh(scopes);
            }
            return true; // Indica que debe reintentar
        }

        if (response.status === 429 && attempt < retries) {
            const retryAfter = response.headers.get('Retry-After');
            const waitTime = retryAfter ? parseInt(retryAfter) * 1000 : this.getRetryDelay(attempt);
            console.warn(`Rate limited, waiting ${waitTime}ms before retry`);
            await this.delay(waitTime);
            return true; // Indica que debe reintentar
        }

        // Para otros errores HTTP, crear error específico
        const errorText = await response.text().catch(() => response.statusText);
        throw new ApiError(
            `HTTP ${response.status}: ${errorText}`,
            response.status,
            response.statusText,
            response
        );
    }

    /**
     * Parsea la respuesta según el tipo de contenido
     */
    private async parseResponse<T>(response: Response): Promise<T> {
        const contentType = response.headers.get('content-type');

        if (contentType?.includes('application/json')) {
            const text = await response.text();
            if (!text) {
                return undefined as T;
            }
            try {
                return JSON.parse(text);
            } catch {
                return text as T;
            }
        } else if (contentType?.includes('text/')) {
            return await response.text() as T;
        } else {
            const text = await response.text();
            if (!text) {
                return undefined as T;
            }
            try {
                return JSON.parse(text);
            } catch {
                return text as T;
            }
        }
    }

    /**
     * Determina si un error debe ser reintentado
     */
    private shouldRetryError(error: Error): boolean {
        return error instanceof TypeError ||
            (error as any)?.name === 'AbortError' ||
            (error instanceof ApiError && [408, 429, 502, 503, 504].includes(error.status));
    }

    /**
     * Ejecuta una petición individual
     */
    private async executeRequest<T>(
        endpoint: string,
        method: string, 
        headers: Record<string, string>, 
        body: any, 
        scopes: string[], 
        timeout: number, 
        useIdToken: boolean, 
        requireAuth: boolean
    ): Promise<ApiResponse<T>> {
        
        const token = requireAuth ? await this.getValidToken(useIdToken, scopes) : null;
        const requestOptions = this.prepareRequestOptions(method, headers, body, token, timeout);
        const url = this.buildUrl(endpoint);

        const response = await fetch(url, requestOptions);

        if (!response.ok) {
            throw response; // Se manejará en el método principal
        }

        const data = await this.parseResponse<T>(response);

        return {
            data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
        };
    }

    /**
     * Procesa un error durante un intento de petición
     */
    private async processRequestError(
        error: any,
        attempt: number,
        retries: number,
        useIdToken: boolean,
        scopes: string[]
    ): Promise<{ shouldRetry: boolean; processedError: Error }> {
        if (error instanceof Response) {
            const shouldRetry = await this.handleHttpError(
                error, attempt, retries, useIdToken, scopes
            );
            return { shouldRetry, processedError: new Error('HTTP Error') };
        }

        const processedError = error instanceof Error ? error : new Error('Unknown error');

        if (attempt >= retries) {
            return { shouldRetry: false, processedError };
        }

        const shouldRetry = this.shouldRetryError(processedError);
        if (shouldRetry) {
            console.warn(`Request failed (attempt ${attempt + 1}), retrying...`, processedError.message);
        }

        return { shouldRetry, processedError };
    }

    /**
     * Ejecuta un intento de petición con manejo de delay
     */
    private async executeAttempt<T>(
        endpoint: string,
        method: string,
        headers: Record<string, string>,
        body: any,
        scopes: string[],
        timeout: number,
        useIdToken: boolean,
        requireAuth: boolean,
        attempt: number
    ): Promise<ApiResponse<T>> {
        if (attempt > 0) {
            await this.delay(this.getRetryDelay(attempt - 1));
        }

        return await this.executeRequest<T>(
            endpoint, method, headers, body, scopes, timeout, useIdToken, requireAuth
        );
    }

    /**
     * Realiza una petición HTTP con reintentos automáticos (con o sin autenticación)
     */
    async request<T = any>(
        endpoint: string,
        options: ApiRequestOptions = {}
    ): Promise<ApiResponse<T>> {
        const {
            method = 'GET',
            headers = {},
            body,
            scopes = this.defaultScopes,
            timeout = this.defaultTimeout,
            retries = this.defaultRetries,
            useIdToken = this.defaultUseIdToken,
            requireAuth = true // Por defecto requiere autenticación
        } = options;

        let lastError: Error | null = null;

        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                return await this.executeAttempt<T>(
                    endpoint, method, headers, body, scopes, timeout, useIdToken, requireAuth, attempt
                );
            } catch (error) {
                const { shouldRetry, processedError } = await this.processRequestError(
                    error, attempt, retries, useIdToken, scopes
                );

                lastError = processedError;

                if (!shouldRetry) break;
            }
        }

        console.error(`All ${retries + 1} attempts failed for ${endpoint}`);
        throw lastError || new Error('Request failed after all retries');
    }

    /**
     * Métodos de conveniencia para diferentes tipos de peticiones
     */

    async get<T = any>(
        endpoint: string,
        options: Omit<ApiRequestOptions, 'method'> = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'GET' });
    }

    async post<T = any>(
        endpoint: string,
        body?: any,
        options: Omit<ApiRequestOptions, 'method' | 'body'> = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'POST', body });
    }

    async put<T = any>(
        endpoint: string,
        body?: any,
        options: Omit<ApiRequestOptions, 'method' | 'body'> = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'PUT', body });
    }

    async patch<T = any>(
        endpoint: string,
        body?: any,
        options: Omit<ApiRequestOptions, 'method' | 'body'> = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
    }

    async delete<T = any>(
        endpoint: string,
        options: Omit<ApiRequestOptions, 'method'> = {}
    ): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...options, method: 'DELETE' });
    }

    /**
     * Métodos de utilidad
     */

    /**
     * Cambia la URL base del servicio
     */
    setBaseUrl(baseUrl: string): void {
        this.baseUrl = baseUrl;
    }

    /**
     * Cambia los scopes por defecto
     */
    setDefaultScopes(scopes: string[]): void {
        this.defaultScopes = scopes;
    }

    /**
     * Configura si usar ID token por defecto
     */
    setUseIdToken(useIdToken: boolean): void {
        this.defaultUseIdToken = useIdToken;
    }

    /**
     * Obtiene la configuración actual de tipo de token
     */
    getUseIdToken(): boolean {
        return this.defaultUseIdToken;
    }

    /**
     * Obtiene información sobre el estado de autenticación
     */
    getAuthState() {
        return this.authService.getAuthState();
    }

    /**
     * Fuerza la renovación de tokens
     */
    async refreshTokens(scopes?: string[], refreshIdToken?: boolean): Promise<void> {
        const targetScopes = scopes || this.defaultScopes;

        if (refreshIdToken || this.defaultUseIdToken) {
            await this.authService.forceIdTokenRefresh(targetScopes);
        } else {
            await this.authService.forceTokenRefresh(targetScopes);
        }
    }

    /**
     * Realiza múltiples peticiones en paralelo con control de concurrencia
     */
    async requestBatch<T = any>(
        requests: Array<{ endpoint: string, options?: ApiRequestOptions }>,
        maxConcurrency: number = 5
    ): Promise<Array<ApiResponse<T> | Error>> {
        const results: Array<ApiResponse<T> | Error> = [];

        for (let i = 0; i < requests.length; i += maxConcurrency) {
            const batch = requests.slice(i, i + maxConcurrency);
            const batchPromises = batch.map(async ({ endpoint, options }) => {
                try {
                    return await this.request<T>(endpoint, options);
                } catch (error) {
                    return error instanceof Error ? error : new Error('Unknown error');
                }
            });

            const batchResults = await Promise.all(batchPromises);
            results.push(...batchResults);
        }

        return results;
    }

    /**
     * Descarga un archivo de forma segura
     */
    async downloadFile(
        endpoint: string,
        filename?: string,
        options: Omit<ApiRequestOptions, 'method'> = {}
    ): Promise<void> {
        const response = await this.request<Blob>(endpoint, { ...options, method: 'GET' });

        // Crear URL temporal para el blob
        const url = window.URL.createObjectURL(response.data);

        // Crear elemento de descarga temporal
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'download';
        document.body.appendChild(link);
        link.click();

        // Limpiar
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}

/**
 * Factory function para crear instancias del servicio
 */
export const createSecureApiService = (
    authService: SecureAuthService,
    baseUrl?: string,
    defaultScopes?: string[],
    useIdToken?: boolean
) => {
    return new SecureApiService(authService, baseUrl, defaultScopes, useIdToken);
};